import PropTypes from "prop-types";
import React, { useReducer, useEffect } from "react";
import axios from "axios";
import { fetchGeofencePackages } from "../shared";

import {
  SET_CURRENT_STEP,
  SET_GEOFENCE_PACKAGES,
  SET_IN_PROGRESS_PACKAGE,
  SET_PACKAGE_STATUS,
  SET_POLLER,
  SET_FINAL_PACKAGE,
  SET_FILE_CONTENT,
  SET_PACKAGE_CREATION_ERROR,
  RESET_PACKAGE_CREATION_FLOW,
  SET_GEOFENCE_PACKAGE_SIZE,
} from "../types";

import geofencePackageReducer, { STEPS } from "./geofencePackageReducer";
import GeofencePackageContext from "./GeofencePackageContext";
import bumpVersion from "../../util/strings/bumpVersion";
import fileNameFromURL from "../../util/strings/fileNameFromUrl";
import API from "../../API";

const GeofencePackageState = ({ children, activeFleet, isPreview }) => {
  const initialState = {
    nextPreviewVersion: null,
    nextFinalVersion: null,
    geofencePackages: null,
    isPreview,
    activeFleet,
    currentStep: isPreview ? STEPS.WARMING_UP : STEPS.VERSION_DIALOG,
    allSteps: STEPS,
    previousStep: null,
    packageStatus: null,
    taskId: null,
    poller: null,
    finalPackage: null,
    fileContentToSave: null,
    error: null,
    packageSize: null,
  };

  let poller;

  const [state, dispatch] = useReducer(geofencePackageReducer, initialState);

  const getMarketGeofencePackages = async (fleet, forceUpdate) => {
    const market = fleet.market?.split("/").pop();
    const [packages] = await fetchGeofencePackages(market ? { market } : {}, forceUpdate);
    return packages.filter(
      (gfp) =>
        !gfp.version.toLowerCase().includes("preview") && activeFleet.uuid4.includes(gfp.fleet),
    );
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /*
   *  Resets the state for generating a new package
   */
  const reset = () => {
    dispatch({
      type: RESET_PACKAGE_CREATION_FLOW,
      payload: {
        currentStep: isPreview ? STEPS.WARMING_UP : STEPS.VERSION_DIALOG,
        taskId: null,
        poller: null,
        finalPackage: null,
        fileContentToSave: null,
        error: null,
        previousStep: null,
        packageStatus: null,
      },
    });
  };

  /*
   *  Sets a failure with an arbitrary error
   */
  const setFailure = (error) => {
    dispatch({
      type: SET_PACKAGE_CREATION_ERROR,
      payload: error,
    });
    dispatch({
      type: SET_CURRENT_STEP,
      payload: STEPS.FAILURE,
    });
  };

  /*
   *  Fetches content from a url and sets the content to
   *  the state variable
   */
  const fetchURLContent = async (url) => {
    let response;
    try {
      response = await axios.get(url);
      dispatch({
        type: SET_FILE_CONTENT,
        payload: {
          content: JSON.stringify(response.data),
          fileName: fileNameFromURL(url),
          mimeType: "text/plain",
        },
      });
    } catch (err) {
      setFailure(err);
    }
  };

  /*
   *  Function to fetch the final geofence packge details
   *  and populate the state
   */
  const setFinalPackage = async () => {
    const fetchFinalPackage = async () => {
      try {
        return await API.geofencePackages.getById(state.taskId);
      } catch (err) {
        return setFailure(err);
      }
    };

    await fetchFinalPackage()
      .then(async (data) => {
        // If the filesets haven't been creaed yet, we need to retry
        if (data.filesets.length === 0) {
          const newData = await sleep(1000).then(async () => fetchFinalPackage());
          return newData;
        }

        return data;
      })
      .then((data) => {
        dispatch({
          type: SET_FINAL_PACKAGE,
          payload: data,
        });
      });
  };

  /*
   *  Initialize a bunch of state
   */
  const initialize = async (fleet) => {
    const packages = await getMarketGeofencePackages(fleet);

    const newestPackage = packages[0] || {
      version: `1.0.0+${fleet.name.toUpperCase().replace(" ", "-")}`,
    };
    const nextFinalVersion = bumpVersion(newestPackage.version, "minor");
    const nextPreviewVersion = `${bumpVersion(newestPackage.version, "patch")}-PREVIEW`;

    dispatch({
      type: SET_GEOFENCE_PACKAGES,
      payload: {
        nextFinalVersion,
        nextPreviewVersion,
        geofencePackages: packages,
      },
    });
  };

  /*
   *  The function that starts binary generation process
   */
  const kickOffPackageCreation = async (version) => {
    try {
      if (version) {
        const response = await API.geofenceApi.uploadGeofenceBinaries(
          version,
          state.activeFleet.uuid4,
        );
        dispatch({
          type: SET_IN_PROGRESS_PACKAGE,
          payload: response,
        });
        dispatch({
          type: SET_CURRENT_STEP,
          payload: STEPS.IN_PROGRESS,
        });
      }
    } catch (error) {
      setFailure(error);
    }
    return false;
  };

  /*
   * Function to get the status of the generation task. Used for polling.
   */
  const getStatusUpdate = async (taskId) => {
    try {
      if (taskId) {
        const response = await API.geofenceApi.getPackageCreationStatus(taskId);
        dispatch({
          type: SET_PACKAGE_STATUS,
          payload: response,
        });
      }
    } catch (error) {
      setFailure(error);
    }
  };

  /*
   *  Effect hook to start polling for status updates
   *  when the task id value is populated
   */
  useEffect(() => {
    if (!state.taskId || poller) {
      return () => {};
    }
    poller = setInterval(async () => {
      await getStatusUpdate(state.taskId);
    }, 1000);

    dispatch({
      type: SET_POLLER,
      payload: poller,
    });

    return () => clearInterval(poller);
  }, [state.taskId]);

  /*
   * Effect hook to initialize the process once we
   * have an active fleet
   */
  useEffect(() => {
    (async () => {
      await initialize(activeFleet);
    })();
  }, [activeFleet]);

  /*
   * Effect hook to handle package status changes
   */
  useEffect(() => {
    if (state.packageStatus?.status === "FAILURE") {
      dispatch({
        type: SET_CURRENT_STEP,
        payload: STEPS.FAILURE,
      });
      if (state.poller) clearInterval(state.poller);
    }

    if (state.packageStatus?.status === "SUCCESS") {
      if (state.poller) clearInterval(state.poller);
      setFinalPackage().then(async () => {
        await sleep(1000);
        dispatch({
          type: SET_CURRENT_STEP,
          payload: STEPS.PACKAGE_COMPLETE,
        });
      });
    }
  }, [state.packageStatus]);

  /*
   *  Effect hook to mark the process as done when this a final package
   *  and the package is complete
   */
  useEffect(() => {
    if (state.currentStep === STEPS.PACKAGE_COMPLETE && !isPreview) {
      sleep(1000).then(() => {
        dispatch({
          type: SET_CURRENT_STEP,
          payload: STEPS.DONE,
        });
      });
    }
  }, [state.currentStep]);

  const calculateBinarySize = async () => {
    const mapFileResponse = await axios.get(state.finalPackage.filesets[0].map_file);
    const mapFileSize = mapFileResponse.headers["content-length"];
    const rulesFileResponse = await axios.get(state.finalPackage.filesets[0].rules_file);
    const rulesFileSize = rulesFileResponse.headers["content-length"];
    const total = parseInt(mapFileSize, 10) + parseInt(rulesFileSize, 10);
    dispatch({
      type: SET_GEOFENCE_PACKAGE_SIZE,
      payload: total,
    });
  };

  /*
   *  Effect hook to handle downloading geojson and rulechain files for preview packages
   */
  useEffect(() => {
    if (!isPreview) return;

    if (state.currentStep === STEPS.PACKAGE_COMPLETE && state.finalPackage?.filesets) {
      (async () => {
        await sleep(1000);
        dispatch({
          type: SET_CURRENT_STEP,
          payload: STEPS.DOWNLOADING_GEOJSON,
        });

        await calculateBinarySize();

        fetchURLContent(state.finalPackage.filesets[0].geojson_file).then(async () => {
          await sleep(1000);
          dispatch({
            type: SET_CURRENT_STEP,
            payload: STEPS.GEOJSON_DOWNLOAD_COMPLETE,
          });
          await sleep(1000);
          dispatch({
            type: SET_CURRENT_STEP,
            payload: STEPS.DOWNLOADING_RULECHAIN,
          });
        });
      })();
    }

    if (state.currentStep === STEPS.DOWNLOADING_RULECHAIN) {
      fetchURLContent(state.finalPackage.filesets[0].rulechains_file)
        .then(async () => {
          await sleep(1000);
          dispatch({
            type: SET_CURRENT_STEP,
            payload: STEPS.RULECHAIN_DOWNLOAD_COMPLETE,
          });
        })
        .then(async () => {
          await sleep(1000);
          dispatch({
            type: SET_CURRENT_STEP,
            payload: STEPS.DONE,
          });
        });
    }
  }, [state.currentStep]);

  /*
   * Effect hook to update the local cache that will populate the packages on the OTA page
   */
  useEffect(() => {
    if (state.packageStatus?.status === "SUCCESS") {
      (async () => {
        await getMarketGeofencePackages(activeFleet, true);
      })();
    }
  }, [state.packageStatus]);

  /*
   * Effect hook to delete the preview package when it's all done
   */
  useEffect(() => {
    if (isPreview && state.currentStep === STEPS.DONE) {
      try {
        API.geofenceApi.deleteGeofencePackage(state.taskId);
      } catch (err) {
        console.warn(err);
      }
    }
  }, [state.currentStep]);

  /*
   * Effect hook to start the preview creation as soon as we have the preview version string
   */
  useEffect(() => {
    if (isPreview && state.currentStep === STEPS.WARMING_UP) {
      setTimeout(() => {
        kickOffPackageCreation(state.nextPreviewVersion);
      }, 1000);
    }
  }, [state.nextPreviewVersion]);

  return (
    <GeofencePackageContext.Provider
      value={{
        ...state,
        kickOffPackageCreation,
        reset,
      }}
    >
      {children}
    </GeofencePackageContext.Provider>
  );
};

GeofencePackageState.propTypes = {
  children: PropTypes.node.isRequired,
  activeFleet: PropTypes.object.isRequired,
  isPreview: PropTypes.bool.isRequired,
};

GeofencePackageState.defaultProps = {};

export default GeofencePackageState;
